import { reduce } from 'lodash';

import analytics from 'common/analytics';

import './calendar-navigation.style.sass';
import template from './calendar-navigation.tpl.pug';

class CalendarNavigationController {
    /* @ngInject */
    constructor($q) {
        this.$q = $q;
    }

    canSelectCalendars() {
        return this.teams && this.teams.length > 0;
    }

    $onInit() {
        this.isSelectingCalendar = this.canSelectCalendars();
    }

    $onChanges(changeObject) {
        if (changeObject.teams) {
            this.isSelectingCalendar = !this.previousTeams || !this.teams || this.previousTeams.length !== this.teams.length
                ? this.canSelectCalendars()
                : this.isSelectingCalendar;

            this.previousTeams = this.teams;
        }

        if (changeObject.user) {
            this.user = this.user || {};
            this.user = changeObject.user.currentValue || this.user;
        }

        if (changeObject.selectedTeam) {
            this.balance = this.getTeamBalance(this.selectedTeam);
            this.sharedWithLabel = this.getSharedWithLabel(this.selectedTeam);
        }

        if (changeObject.plan) {
            this.planBadgeStyle = this.getPlanBadgeStyle(this.plan);
        }
    }

    getTeamBalance(team) {
        return team && team.calendars && team.calendars.length > 0
            ? reduce(team.calendars, (balance, calendar) => balance + calendar.balance, 0)
            : 0;
    }

    getSharedWithLabel(team) {
        const members = this.getTeamMembers(team);
        const filteredMembers = this.removeCurrentUserFromMembers(members);

        if (!filteredMembers || filteredMembers.length === 0) {
            return null;
        }

        const displayNames = filteredMembers
            .map((member) => member && (member.name || member.email))
            .filter((name) => !!name);

        const firstTwo = displayNames.slice(0, 2);

        if (filteredMembers.length === 1) {
            return `Shared with: ${firstTwo[0]}`;
        }

        if (filteredMembers.length === 2) {
            return `Shared with: ${firstTwo.join(', ')}`;
        }

        const remaining = Math.max(filteredMembers.length - 2, 0);
        return `Shared with: ${firstTwo.join(', ')} and ${remaining} more`;
    }

    getTeamMembers(team) {
        if (!team) {
            return [];
        }

        if (Array.isArray(team.members)) {
            return team.members;
        }

        if (Array.isArray(team.collaborators)) {
            return team.collaborators;
        }

        return [];
    }

    getPlanBadgeStyle(plan) {
        const color = plan && plan.color ? plan.color : null;
        if (!color) {
            return null;
        }

        const textColor = this.getReadableTextColor(color);
        return {
            backgroundColor: color,
            color: textColor
        };
    }

    getReadableTextColor(backgroundColor) {
        const rgb = this.parseColorToRgb(backgroundColor);
        if (!rgb) {
            return '#fff';
        }

        const [r, g, b] = rgb.map((value) => {
            const channel = value / 255;
            return channel <= 0.03928
                ? channel / 12.92
                : Math.pow((channel + 0.055) / 1.055, 2.4);
        });

        const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        return luminance > 0.5 ? '#394852' : '#fff';
    }

    parseColorToRgb(color) {
        if (!color || typeof color !== 'string') {
            return null;
        }

        let value = color.trim();
        if (value[0] === '#') {
            value = value.slice(1);
        }

        if (value.length === 3) {
            const red = parseInt(value[0] + value[0], 16);
            const green = parseInt(value[1] + value[1], 16);
            const blue = parseInt(value[2] + value[2], 16);
            return [red, green, blue];
        }

        if (value.length === 6) {
            const red = parseInt(value.slice(0, 2), 16);
            const green = parseInt(value.slice(2, 4), 16);
            const blue = parseInt(value.slice(4, 6), 16);
            return [red, green, blue];
        }

        return null;
    }

    removeCurrentUserFromMembers(members) {
        if (!members || members.length === 0) {
            return [];
        }

        const user = this.user;
        if (!user) {
            return members;
        }

        return members.filter((member) => {
            if (!member) {
                return false;
            }

            if (user.id && member.id) {
                return member.id !== user.id;
            }

            if (user.email && member.email) {
                return member.email !== user.email;
            }

            return true;
        });
    }

    subscribe(renew) {
        if (renew) {
            return analytics.trackPlanRenewFromSidebar();
        }

        return analytics.trackPlanSubscribeFromSidebar();
    }

    toggleView() {
        if (this.canSelectCalendars()) {
            this.isSelectingCalendar = !this.isSelectingCalendar;
        }
    }

    editCalendar(team, calendar) {
        return this.onCalendarEdit({
            team,
            calendar
        });
    }

    deleteCalendar(team, calendar) {
        return this.onCalendarDelete({
            team,
            calendar
        });
    }

    addCalendar(team) {
        return this.onCalendarAdd({
            team
        });
    }

    selectCalendar(team, calendar) {
        return this.onCalendarSelect({
            team,
            calendar
        });
    }

    reorderCalendars(team, order) {
        return this.onCalendarReorder({
            team,
            order
        });
    }

    selectTeam(team) {
        return this.$q.when(this.onTeamSelect({
            team
        })).finally(() => this.isSelectingCalendar = true);
    }

    editTeam(team) {
        return this.onTeamEdit({
            team
        });
    }

    addTeam() {
        return this.onTeamAdd();
    }

    toggleMultiple(multiple) {
        return this.onMultipleChange({
            multiple
        });
    }

    reorderTeams(order) {
        return this.onTeamReorder({
            order
        });
    }
}

export default {
    selector: 'dbACalendarNavigation',
    template: template(),
    controller: CalendarNavigationController,
    bindings: {
        loading: '<',
        plan: '<',
        user: '<',
        selectedCalendarIds: '<',
        selectedTeam: '<',
        teams: '<',
        multiple: '<',
        canSelectMultiple: '<',
        balanceWarningThreshold: '<',
        onCalendarEdit: '&',
        onCalendarAdd: '&',
        onCalendarDelete: '&',
        onCalendarSelect: '&',
        onCalendarReorder: '&',
        onTeamSelect: '&',
        onTeamEdit: '&',
        onTeamAdd: '&',
        onTeamReorder: '&',
        onMultipleChange: '&',
        onSettingsOpen: '&'
    }
};



// WEBPACK FOOTER //
// ./src/application/scene/authorized/scene/calendar/component/calendar-navigation/calendar-navigation.cmpt.js